import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import VisibilityIcon from '@mui/icons-material/Visibility';
import SupervisorAccountIcon from '@mui/icons-material/SupervisorAccount';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import {
  deleteCorporateEmployee,
  getCorporateEmployees,
  getCostCenterOptions,
  getPolicyOptions,
} from '../../../../api/endpoints/corporateEmployee';
import type { CorporateEmployee } from '../../../../api/types/corporateEmployee';
import { ApiErrorAlert } from '../../../../components/ApiErrorAlert';
import { ConfirmDialog } from '../../../../components/ConfirmDialog';
import { EmployeeFormDialog } from '../components/EmployeeFormDialog';
import { ExcelImportDialog } from '../components/ExcelImportDialog';
import { ManagerAssignDialog } from '../components/ManagerAssignDialog';

/** Personel Listesi — table-based list (not @mui/x-data-grid — incompatible with this
 *  project's pinned TypeScript 4.9.5, see other Phase 1/2 modules for the same note).
 *  Search/filter is done client-side over the full `GetCorporateEmployees` result:
 *  `GetCorporateEmployeeByQuery` is confirmed broken server-side (NullReferenceException
 *  for every input tried) — see api/types/corporateEmployee.ts. */
export default function EmployeeListPage() {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const [editing, setEditing] = useState<CorporateEmployee | null>(null);
  const [deleting, setDeleting] = useState<CorporateEmployee | null>(null);
  const [assigningManagerFor, setAssigningManagerFor] = useState<CorporateEmployee | null>(null);
  const [importOpen, setImportOpen] = useState(false);
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  const query = useQuery({ queryKey: ['corporateEmployee', 'list'], queryFn: () => getCorporateEmployees() });
  const policiesQuery = useQuery({ queryKey: ['corporateEmployee', 'policyOptions'], queryFn: () => getPolicyOptions() });
  const costCentersQuery = useQuery({
    queryKey: ['corporateEmployee', 'costCenterOptions'],
    queryFn: () => getCostCenterOptions(),
  });

  const employees: CorporateEmployee[] = useMemo(
    () => query.data?.corporateEmployeeList ?? [],
    [query.data]
  );
  const policies = policiesQuery.data?.policies ?? [];
  const costCenters = costCentersQuery.data?.costCenter ?? [];

  const rows = useMemo(() => {
    const term = search.trim().toLocaleLowerCase('tr');
    if (!term) return employees;
    return employees.filter((e) =>
      [e.name, e.surName, e.emailAdress, e.registrationNumber, e.phoneNumber]
        .filter(Boolean)
        .some((field) => field!.toLocaleLowerCase('tr').includes(term))
    );
  }, [employees, search]);

  const deleteMutation = useMutation({
    mutationFn: (employee: CorporateEmployee) => deleteCorporateEmployee(employee),
    onSuccess: () => {
      enqueueSnackbar('Personel silindi.', { variant: 'success' });
      queryClient.invalidateQueries({ queryKey: ['corporateEmployee', 'list'] });
      setDeleting(null);
    },
    onError: (err) => enqueueSnackbar(err instanceof Error ? err.message : 'İşlem başarısız.', { variant: 'error' }),
  });

  return (
    <Box>
      <Stack direction="row" sx={{ justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
        <Typography variant="h5">Kurumsal Personel</Typography>
        <Stack direction="row" spacing={1}>
          <Button variant="outlined" startIcon={<UploadFileIcon />} onClick={() => setImportOpen(true)}>
            Excel'den İçe Aktar
          </Button>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => setCreateOpen(true)}>
            Yeni Personel
          </Button>
        </Stack>
      </Stack>

      <TextField
        size="small"
        placeholder="Ad, soyad, e-posta, sicil no veya telefon ara…"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        sx={{ mb: 2, minWidth: 320 }}
      />

      {query.isError && <ApiErrorAlert error={query.error} sx={{ mb: 2 }} />}
      {query.isFetching && <CircularProgress size={24} sx={{ mb: 2 }} />}

      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Ad Soyad</TableCell>
              <TableCell>E-posta</TableCell>
              <TableCell>Politika</TableCell>
              <TableCell>Masraf Merkezi</TableCell>
              <TableCell>Yönetici</TableCell>
              <TableCell>Durum</TableCell>
              <TableCell>KVKK</TableCell>
              <TableCell align="right" />
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.length === 0 && !query.isFetching && (
              <TableRow>
                <TableCell colSpan={8}>
                  <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
                    Kayıtlı personel bulunamadı.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
            {rows.map((row) => (
              <TableRow key={row.employeeID} hover>
                <TableCell>
                  {row.name} {row.surName}
                </TableCell>
                <TableCell>{row.emailAdress ?? '—'}</TableCell>
                <TableCell>{row.policyName || '—'}</TableCell>
                <TableCell>{row.costCenterName || '—'}</TableCell>
                <TableCell>{row.managerName || '—'}</TableCell>
                <TableCell>
                  <Chip size="small" color={row.isActive ? 'success' : 'default'} label={row.isActive ? 'Aktif' : 'Pasif'} />
                </TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    color={row.kvkkAccepted ? 'success' : 'warning'}
                    label={row.kvkkAccepted ? 'Onaylı' : 'Bekliyor'}
                  />
                </TableCell>
                <TableCell align="right">
                  <Tooltip title="Detay">
                    <IconButton size="small" onClick={() => navigate(`/corporate/employees/${row.employeeID}`)} aria-label="Detay">
                      <VisibilityIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Yönetici Ata">
                    <IconButton size="small" onClick={() => setAssigningManagerFor(row)} aria-label="Yönetici Ata">
                      <SupervisorAccountIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <IconButton size="small" onClick={() => setEditing(row)} aria-label="Düzenle">
                    <EditIcon fontSize="small" />
                  </IconButton>
                  <IconButton size="small" onClick={() => setDeleting(row)} aria-label="Sil">
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <EmployeeFormDialog
        open={createOpen}
        employee={null}
        policies={policies}
        costCenters={costCenters}
        onClose={() => setCreateOpen(false)}
      />
      <EmployeeFormDialog
        open={!!editing}
        employee={editing}
        policies={policies}
        costCenters={costCenters}
        onClose={() => setEditing(null)}
      />
      <ManagerAssignDialog
        open={!!assigningManagerFor}
        employee={assigningManagerFor}
        candidates={employees}
        onClose={() => setAssigningManagerFor(null)}
      />
      <ExcelImportDialog open={importOpen} onClose={() => setImportOpen(false)} />

      <ConfirmDialog
        open={!!deleting}
        title="Personeli sil"
        description={`"${deleting?.name ?? ''} ${deleting?.surName ?? ''}" adlı personeli silmek istediğinize emin misiniz?`}
        loading={deleteMutation.isPending}
        onConfirm={() => deleting && deleteMutation.mutate(deleting)}
        onCancel={() => setDeleting(null)}
      />
    </Box>
  );
}
